import { FormCountry, FormInput } from "@ac-expo/ui-components";
import ToggleableFormInput from "@ui/components/ui/form/ToggleableFormInput";
import { Control, FieldValues, Path, UseFormSetValue } from "react-hook-form";

type ProfessionalFieldsProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  siretName: Path<TFieldValues>;
  vatName: Path<TFieldValues>;
  businessNameName: Path<TFieldValues>;
  countryName: Path<TFieldValues>;
  isSiretVisible: boolean;
  isVATVisible: boolean;
  onSiretVisibilityChange: (visible: boolean) => void;
  onVATVisibilityChange: (visible: boolean) => void;
};

export const ProfessionalFields = <TFieldValues extends FieldValues>({
  control,
  setValue,
  siretName,
  vatName,
  businessNameName,
  countryName,
  isSiretVisible,
  isVATVisible,
  onSiretVisibilityChange,
  onVATVisibilityChange,
}: ProfessionalFieldsProps<TFieldValues>) => {
  const handleSiretCheckedChange = (checked: boolean) => {
    if (checked) {
      setValue(vatName, undefined as TFieldValues[Path<TFieldValues>]);
      onVATVisibilityChange(false);
    }
    onSiretVisibilityChange(!isSiretVisible);
  };

  const handleVATCheckedChange = (checked: boolean) => {
    if (!checked) {
      onSiretVisibilityChange(true);
    }
    onVATVisibilityChange(!isVATVisible);
  };

  return (
    <div className="space-y-4 rounded-lg bg-gray-50 p-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <ToggleableFormInput
          control={control}
          name={siretName}
          label="SIRET ou SIREN"
          placeholder="SIRET ou SIREN"
          resetField={() =>
            setValue(siretName, undefined as TFieldValues[Path<TFieldValues>])
          }
          toggleText="Je ne dispose pas de n°siret/siren"
          hasMessage
          initialVisible={isSiretVisible}
          key={`siret-${isSiretVisible}`}
          onCheckedChange={handleSiretCheckedChange}
          maxLength={14}
        />
        <ToggleableFormInput
          control={control}
          name={vatName}
          label="TVA"
          placeholder="TVA"
          resetField={() =>
            setValue(vatName, undefined as TFieldValues[Path<TFieldValues>])
          }
          toggleText="Je ne dispose pas de n° de T.V.A."
          hasMessage
          initialVisible={isVATVisible}
          key={`vat-${isVATVisible}`}
          onCheckedChange={handleVATCheckedChange}
          maxLength={13}
        />
      </div>
      <FormInput
        control={control}
        name={businessNameName}
        label="Raison sociale (facultatif)"
        placeholder="Raison sociale"
        maxLength={64}
      />
      <FormCountry control={control} name={countryName} label="Pays" />
    </div>
  );
};
